import { z } from 'zod';
import { GrantablePermissionSchema } from './api-keys.schemas';

/**
 * The connected-client surface (IMM-601.8.2): the OAuth grants a member's MCP
 * clients hold, listed and revoked from **Einstellungen → MCP**.
 *
 * Deliberately NOT part of `api-keys.schemas.ts`: a grant has no secret to show
 * once, no rotation, no expiry the member picks — it has a third-party client
 * name and an end by revocation. It has its own member-implicit permission
 * triple (`workspace:identity:connected-client-*`) rather than the api-key pair,
 * because every member may connect a client that acts as themselves.
 */
/**
 * How far a connected client's grant reaches. `ALL_TOOLS` follows the grantable
 * catalogue as deployed; `SELECTED_PERMISSIONS` stays on the list consented to.
 */
export const CONNECTED_CLIENT_ACCESS = ['ALL_TOOLS', 'SELECTED_PERMISSIONS'] as const;
export const ConnectedClientAccessSchema = z.enum(CONNECTED_CLIENT_ACCESS);
export type ConnectedClientAccess = z.infer<typeof ConnectedClientAccessSchema>;

const WorkspaceIdPathParamsSchema = z.object({ workspaceId: z.uuid() });

/**
 * Never carries a token, a token prefix, or the issuer's grant id: the row is
 * addressed by OUR id, and nothing a client could replay leaves the backend.
 */
export const ConnectedClientListItemSchema = z.object({
  id: z.uuid(),
  clientName: z.string(),
  access: ConnectedClientAccessSchema,
  /** Empty when `access` is `ALL_TOOLS`: an unscoped grant has no list to report. */
  grantedPermissions: z.array(z.string()),
  connectedAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().nullable(),
});

export type ConnectedClientListItem = z.infer<typeof ConnectedClientListItemSchema>;

export const ListConnectedClientsPathParamsSchema = WorkspaceIdPathParamsSchema;

export const ListConnectedClientsResponseSchema = z.object({
  data: z.array(ConnectedClientListItemSchema),
});

export type ListConnectedClientsResponse = z.infer<typeof ListConnectedClientsResponseSchema>;

export const RevokeConnectedClientPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  connectedClientId: z.uuid(),
});

export const UpdateConnectedClientAccessPathParamsSchema = RevokeConnectedClientPathParamsSchema;

export const UpdateConnectedClientAccessBodySchema = z.discriminatedUnion('access', [
  z.object({ access: z.literal('ALL_TOOLS') }),
  z.object({ access: z.literal('SELECTED_PERMISSIONS'), permissions: z.array(GrantablePermissionSchema).min(1) }),
]);

export type UpdateConnectedClientAccessBody = z.infer<typeof UpdateConnectedClientAccessBodySchema>;

/*
 * The consent bridge — the three leaves that turn "a client sent a browser at
 * our authorize endpoint" into a `connected_clients` grant, without the member
 * ever pasting a secret.
 *
 * The QUERY is one id (IMM-601.8.3). It used to accept the issuer's redirect
 * wholesale, because which parameters a vendor appended was an unverified guess
 * quarantined in one adapter. immoOS is the authorization server now: it wrote
 * the `oauth_authorization_requests` row itself, so the screen carries a
 * reference to server-held state rather than the state itself.
 */
export const PrepareOauthConsentQuerySchema = z.object({
  /** The `oauth_authorization_requests` row id, minted by `GET /v1/oauth/authorize`. */
  request_id: z.uuid(),
});

/**
 * What the consent screen renders. Everything here is a REQUEST, never an
 * entitlement: the cap to what the member actually holds is applied server-side
 * at approval and re-applied on every subsequent request (IMM-601.8.1).
 */
export const PrepareOauthConsentResponseSchema = z.object({
  /**
   * The authorization request's id, echoed back verbatim by approve and decline.
   *
   * It used to be an unsealed base64 payload carrying the scopes themselves,
   * which needed three separate arguments for why a browser rewriting it was
   * harmless (IMM-601.8.2). It is now a REFERENCE to a row this server wrote:
   * unguessable, single-use, and naming scopes whoever holds it cannot rewrite.
   * The field keeps its name so the screen and both leaves did not have to move.
   */
  handle: z.string(),
  /** The registered client's display name. Nullable for shape stability only. */
  clientName: z.string().nullable(),
  /**
   * Asked for AND grantable — the subset the member could conceivably approve.
   * Typed `string` like `ConnectedClientListItemSchema.grantedPermissions` and
   * for the same reason: these are read-only projections the screen labels
   * through the shared permission catalogue, and a narrowed enum here would make
   * every added permission literal a breaking change to a display field.
   */
  requestedScopes: z.array(z.string()),
  /**
   * Asked for and NOT grantable by anybody on this platform. Kept rather than
   * dropped so the screen can say so: a client that silently gets less than it
   * asked for has no way to tell a demoted member from an impossible request.
   */
  unsupportedScopes: z.array(z.string()),
});

export type PrepareOauthConsentResponse = z.infer<typeof PrepareOauthConsentResponseSchema>;

export const CompleteOauthConsentPathParamsSchema = WorkspaceIdPathParamsSchema;

export const CompleteOauthConsentBodySchema = z.object({
  /** The authorization request id, echoed back verbatim from the prepare leaf. */
  handle: z.uuid(),
  /**
   * Defaults to `SELECTED_PERMISSIONS` so a SPA bundle cached across the deploy,
   * which does not send the field, keeps today's behaviour.
   */
  access: ConnectedClientAccessSchema.default('SELECTED_PERMISSIONS'),
  /**
   * What the member ticked; ignored when `access` is `ALL_TOOLS`. A CEILING is
   * applied twice on the way in — first against the scopes the handle says were
   * requested, then against the member's own live permissions — so a rewritten
   * body can only ever narrow the grant.
   */
  approvedScopes: z.array(GrantablePermissionSchema),
});

export type CompleteOauthConsentBody = z.infer<typeof CompleteOauthConsentBodySchema>;

export const CompleteOauthConsentResponseSchema = z.object({
  /**
   * The client's REGISTERED redirect, carrying `code`, the `state` verbatim as
   * the client sent it, and `iss` (RFC 9207). Ours to build now that we are the
   * authorization server — never a value the browser supplied.
   */
  redirectUri: z.url(),
  access: ConnectedClientAccessSchema,
  /**
   * What was ACTUALLY granted, after both caps; empty when `access` is
   * `ALL_TOOLS`. The screen has already shown the member which of the requested
   * permissions it could not grant; this is the server's own account of the
   * outcome, so the two cannot silently disagree.
   */
  grantedPermissions: z.array(z.string()),
});

export type CompleteOauthConsentResponse = z.infer<typeof CompleteOauthConsentResponseSchema>;

export const DeclineOauthConsentPathParamsSchema = WorkspaceIdPathParamsSchema;

export const DeclineOauthConsentBodySchema = z.object({
  /** The same authorization request id approve carries. */
  handle: z.uuid(),
});

export type DeclineOauthConsentBody = z.infer<typeof DeclineOauthConsentBodySchema>;

/**
 * A decline is a PROTOCOL OUTCOME now, not a navigation (IMM-601.8.3). The
 * shipped Cancel wrote nothing and went back to Einstellungen, which was right
 * when a vendor owned the flow and we had nowhere to report to — a client was
 * simply left waiting. We own the flow, so the client is told `access_denied`
 * and stops.
 */
export const DeclineOauthConsentResponseSchema = z.object({
  /** The registered redirect carrying `error=access_denied`, the `state` and `iss`. */
  redirectUri: z.url(),
});

export type DeclineOauthConsentResponse = z.infer<typeof DeclineOauthConsentResponseSchema>;
